import { ReactNode, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAxiosInstance } from "src/hooks/useAxiosInstance";
import { APIS } from "src/utils/constants";
import {
  PermissionsContext,
  PermissionsContextValue,
  TableOp,
  UserTablePermission,
} from "./PermissionsContext";

interface RawPermissionRow {
  tableName: string;
  canSelect: boolean;
  canInsert: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

/**
 * Mounts at the root of the authenticated app tree, fetches the JWT
 * user's RP_ROLE_TABLES grants once, and exposes them via context.
 *
 * One round-trip per session — react-query keeps the result in its
 * cache so navigations / re-renders don't refetch. If the user opens
 * a second tab after an admin changes their role, they pick the new
 * grants up on next login or hard refresh; that matches the legacy
 * Forms behaviour where grants were re-read each time a form opened.
 */
export function PermissionsProvider({ children }: { children: ReactNode }) {
  const axiosInstance = useAxiosInstance();

  const { data, isLoading, error, refetch } = useQuery<UserTablePermission[]>({
    queryKey: ["user-permissions"],
    queryFn: async () => {
      const res = await axiosInstance.get<RawPermissionRow[]>(APIS.UserPermissions);
      return (res.data ?? []).map((r) => ({
        tableName: r.tableName,
        canSelect: !!r.canSelect,
        canInsert: !!r.canInsert,
        canUpdate: !!r.canUpdate,
        canDelete: !!r.canDelete,
      }));
    },
    // Permissions don't change without an explicit refresh — staleTime
    // = Infinity avoids background refetch noise. refresh() below
    // forces a re-fetch when the caller needs to pick up a change.
    staleTime: Infinity,
    retry: 1,
  });

  const permissions = useMemo(() => data ?? [], [data]);

  // Index by upper-cased table name for O(1) lookups; legacy table
  // names are uppercase but FE call sites often pass mixed-case.
  const byName = useMemo(() => {
    const map = new Map<string, UserTablePermission>();
    for (const row of permissions) {
      map.set(row.tableName.toUpperCase(), row);
    }
    return map;
  }, [permissions]);

  const can = useCallback(
    (tableName: string, op: TableOp): boolean => {
      const row = byName.get(tableName.toUpperCase());
      if (!row) return false;
      switch (op) {
        case "Select":
          return row.canSelect;
        case "Insert":
          return row.canInsert;
        case "Update":
          return row.canUpdate;
        case "Delete":
          return row.canDelete;
        default:
          return false;
      }
    },
    [byName],
  );

  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const value: PermissionsContextValue = useMemo(
    () => ({
      permissions,
      isLoading,
      error: (error as Error) ?? null,
      can,
      refresh,
    }),
    [permissions, isLoading, error, can, refresh],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}
